/* Lightweight bilingual (EN/TR) support for the public marketing surface.
 * English is the default and the source of truth; Turkish is opt-in via the
 * /tr landing route or the EN|TR switcher (preference in localStorage).
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const LANG_KEY = "elh_lang";
const SUGGEST_KEY = "elh_lang_suggest_dismissed";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LangContext = createContext({ lang: "en", setLang: () => {}, T: (en) => en });

export function getStoredLang() {
    try {
        const v = localStorage.getItem(LANG_KEY);
        return v === "tr" ? "tr" : "en";
    } catch {
        return "en";
    }
}

export function trackPublic(kind, meta = {}) {
    try {
        axios.post(`${API}/public/track`, { kind, meta: { ...meta, path: window.location.pathname } }).catch(() => {});
    } catch {
        /* never block UI on analytics */
    }
}

export function LanguageProvider({ children }) {
    const [lang, setLangState] = useState(getStoredLang);
    const setLang = useCallback((next, source = "switcher") => {
        setLangState(next);
        try {
            localStorage.setItem(LANG_KEY, next);
        } catch { /* ignore */ }
        trackPublic("lang_switch", { lang: next, target: source });
    }, []);
    const T = useCallback((en, tr) => (lang === "tr" && tr ? tr : en), [lang]);
    return (
        <LangContext.Provider value={{ lang, setLang, setLangLocal: setLangState, T }}>
            {children}
        </LangContext.Provider>
    );
}

export const useLang = () => useContext(LangContext);

/* Small dismissible "Türkçe görüntüle" suggestion for Turkish browsers.
 * Never redirects; only suggests. */
export function LangSuggestion() {
    const { lang } = useLang();
    const [show, setShow] = useState(false);
    useEffect(() => {
        try {
            const dismissed = localStorage.getItem(SUGGEST_KEY) === "1";
            const isTrBrowser = (navigator.language || "").toLowerCase().startsWith("tr");
            const onTr = window.location.pathname.startsWith("/tr");
            if (isTrBrowser && !dismissed && lang !== "tr" && !onTr) {
                setShow(true);
                trackPublic("tr_suggestion_shown", { lang: "en" });
            }
        } catch { /* ignore */ }
    }, [lang]);
    if (!show) return null;
    const dismiss = () => {
        try { localStorage.setItem(SUGGEST_KEY, "1"); } catch { /* ignore */ }
        setShow(false);
    };
    return (
        <div
            className="bg-[#C9A84C] text-[#0D1B3E] text-sm px-4 py-2.5 flex items-center justify-center gap-4 flex-wrap"
            data-testid="lang-suggestion"
        >
            <span>Bu sayfa Türkçe olarak da mevcuttur.</span>
            <a
                href="/tr"
                className="underline font-semibold min-h-[32px] inline-flex items-center"
                data-testid="lang-suggestion-accept"
                onClick={() => {
                    try { localStorage.setItem(LANG_KEY, "tr"); } catch { /* ignore */ }
                    trackPublic("tr_suggestion_accepted", { lang: "tr" });
                }}
            >
                Türkçe görüntüle
            </a>
            <button
                type="button"
                onClick={dismiss}
                aria-label="Kapat"
                className="font-semibold min-h-[32px] min-w-[32px]"
                data-testid="lang-suggestion-dismiss"
            >
                ✕
            </button>
        </div>
    );
}

/* EN | TR switcher. On the landing page it navigates between / and /tr;
 * elsewhere it only switches the stored preference. */
export function LangSwitcher({ dark = true, testId = "lang-switcher" }) {
    const { lang, setLang } = useLang();
    const choose = (next) => {
        if (next === lang && !((next === "tr") !== window.location.pathname.startsWith("/tr"))) return;
        setLang(next);
        const path = window.location.pathname;
        if (path === "/" && next === "tr") window.location.assign("/tr");
        else if (path.startsWith("/tr") && next === "en") window.location.assign("/");
    };
    const base = dark ? "text-[#F5F0E8]/70 hover:text-[#C9A84C]" : "text-[#0D1B3E]/70 hover:text-[#C9A84C]";
    const on = dark ? "text-[#C9A84C] font-semibold" : "text-[#0D1B3E] font-semibold";
    return (
        <div className={`inline-flex items-center gap-1 text-xs tracking-[0.18em] uppercase ${base}`} data-testid={testId}>
            <button type="button" onClick={() => choose("en")} data-testid={`${testId}-en`}
                className={`min-h-[44px] px-1.5 ${lang === "en" ? on : ""}`}>EN</button>
            <span className="opacity-40">|</span>
            <button type="button" onClick={() => choose("tr")} data-testid={`${testId}-tr`}
                className={`min-h-[44px] px-1.5 ${lang === "tr" ? on : ""}`}>TR</button>
        </div>
    );
}
